import React, { useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { API_BASE_URL } from '../../lib/apiConfig';
import { apiFetch } from '../../lib/apiFetch';
import { useAuth } from '../../contexts/AuthContext';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface DeviceContact {
  name: string;
  phone: string; // normalized (digits only, last 9)
  rawPhone: string; // original from device
  rawName: string; // original from device
}

interface SingleEditState {
  contact: DeviceContact;
  editedName: string;
  editedPhone: string;
  skipCity: boolean;
}

interface Props {
  contacts: DeviceContact[]; // unmatched contacts from device
  onClose: () => void;
  onImported: (count: number) => void;
}

// ─── Animations ──────────────────────────────────────────────────────────────

const slideUp = keyframes`
  from { transform: translateY(100%); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

// ─── Styled components ───────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(10, 15, 28, 0.55);
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease;
  backdrop-filter: blur(2px);
`;

const Sheet = styled.div`
  position: fixed;
  left: 0; right: 0; bottom: 0;
  max-height: 90dvh;
  background: #fff;
  border-radius: 20px 20px 0 0;
  display: flex;
  flex-direction: column;
  z-index: 1001;
  animation: ${slideUp} 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;
`;

const Handle = styled.div`
  width: 36px; height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  margin: 10px auto 0;
  flex-shrink: 0;
`;

const SheetHeader = styled.div`
  padding: 16px 20px 12px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
`;

const SheetTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #0a0f1c;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TealDot = styled.div`
  width: 8px; height: 8px;
  border-radius: 50%;
  background: #0E8C7F;
  flex-shrink: 0;
`;

const SheetSub = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
`;

const SelectAllRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
`;

const SelectAllLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #0a0f1c;
  user-select: none;
`;

const CheckBox = styled.input.attrs({ type: 'checkbox' })`
  width: 18px; height: 18px;
  accent-color: #0E8C7F;
  cursor: pointer;
  flex-shrink: 0;
`;

const SelectedCount = styled.div`
  margin-left: auto;
  font-size: 12px;
  color: #64748b;
`;

const ListBody = styled.div`
  overflow-y: auto;
  flex: 1;
  padding: 8px 0 16px;
`;

const AlphaSection = styled.div``;

const AlphaLabel = styled.div`
  padding: 6px 20px 2px;
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const ContactRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 20px;
  transition: background 0.15s;
  &:active { background: #f8fafc; }
`;

const ContactAvatar = styled.div<{ $color: string }>`
  width: 36px; height: 36px;
  border-radius: 50%;
  background: ${p => p.$color};
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ContactInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ContactName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #0a0f1c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ContactPhone = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 1px;
`;

const PencilBtn = styled.button`
  width: 32px; height: 32px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;
  &:active { background: #e0f2f0; color: #0E8C7F; }
`;

const SheetFooter = styled.div`
  padding: 14px 20px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  gap: 10px;
  flex-shrink: 0;
  background: #fff;
`;

const CancelBtn = styled.button`
  flex: 1;
  height: 44px;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`;

const ImportBtn = styled.button`
  flex: 2;
  height: 44px;
  border: none;
  border-radius: 12px;
  background: #0E8C7F;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: opacity 0.15s;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Spinner = styled.div`
  width: 16px; height: 16px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

// ─── Single-contact edit sheet ────────────────────────────────────────────────

const EditSheet = styled(Sheet)`
  max-height: 60dvh;
`;

const EditBody = styled.div`
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
`;

const EditLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 4px;
`;

const EditInput = styled.input`
  width: 100%;
  height: 44px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  padding: 0 12px;
  font-size: 15px;
  color: #0a0f1c;
  background: #fff;
  outline: none;
  &:focus { border-color: #0E8C7F; box-shadow: 0 0 0 3px rgba(14,140,127,0.12); }
`;

const ToggleRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
`;

const ToggleLabel = styled.div`
  font-size: 14px;
  color: #0a0f1c;
  flex: 1;
`;

const ToggleSub = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const Toggle = styled.input.attrs({ type: 'checkbox' })`
  width: 44px; height: 24px;
  accent-color: #0E8C7F;
  cursor: pointer;
`;

// ─── Avatar colors ────────────────────────────────────────────────────────────

const AVATAR_COLORS = ['#6366f1','#8b5cf6','#ec4899','#f59e0b','#10b981','#3b82f6','#ef4444','#14b8a6'];
const avatarColor = (name: string) => AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
const initials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const formatPhoneDisplay = (p: string) => {
  const d = p.replace(/\D/g, '');
  if (d.length === 11) return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
  return p;
};

// ─── Component ───────────────────────────────────────────────────────────────

const ContactSyncModal: React.FC<Props> = ({ contacts, onClose, onImported }) => {
  const { session } = useAuth();
  const [selected, setSelected] = useState<Set<number>>(new Set(contacts.map((_, i) => i)));
  const [loading, setLoading] = useState(false);
  const [singleEdit, setSingleEdit] = useState<SingleEditState | null>(null);

  // Group contacts alphabetically
  const grouped = React.useMemo(() => {
    const map = new Map<string, { contact: DeviceContact; idx: number }[]>();
    contacts.forEach((c, idx) => {
      const letter = c.rawName.trim()[0]?.toUpperCase() || '#';
      const key = /[A-Z]/.test(letter) ? letter : '#';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push({ contact: c, idx });
    });
    return new Map(Array.from(map.entries()).sort((a, b) => {
      if (a[0] === '#') return 1;
      if (b[0] === '#') return -1;
      return a[0].localeCompare(b[0]);
    }));
  }, [contacts]);

  const toggleAll = () => {
    if (selected.size === contacts.length) setSelected(new Set());
    else setSelected(new Set(contacts.map((_, i) => i)));
  };

  const toggleOne = (idx: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const handleBatchImport = useCallback(async () => {
    const chosen = contacts
      .filter((_, i) => selected.has(i))
      .map(c => ({ nome: c.rawName, telefone: c.rawPhone }));

    if (chosen.length === 0) return;

    setLoading(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/v1/eleitores/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ contacts: chosen }),
      });
      if (!res.ok) throw new Error('Falha ao importar');
      const data = await res.json();
      onImported(data.created || chosen.length);
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [contacts, selected, session, onImported, onClose]);

  const handleSingleSave = useCallback(async () => {
    if (!singleEdit) return;
    setLoading(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/v1/eleitores/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          contacts: [{ nome: singleEdit.editedName, telefone: singleEdit.editedPhone }],
        }),
      });
      if (!res.ok) throw new Error('Falha ao salvar');
      onImported(1);
      setSingleEdit(null);
      // Remove from list
      const idx = contacts.indexOf(singleEdit.contact);
      setSelected(prev => { const next = new Set(prev); next.delete(idx); return next; });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [singleEdit, contacts, session, onImported]);

  return (
    <>
      <Overlay onClick={singleEdit ? undefined : onClose} />

      {/* Main list sheet */}
      <Sheet style={{ display: singleEdit ? 'none' : 'flex' }}>
        <Handle />
        <SheetHeader>
          <SheetTitle>
            <TealDot />
            {contacts.length} contatos do telefone
          </SheetTitle>
          <SheetSub>Selecione quem deseja importar. Você pode completar a cidade depois.</SheetSub>
        </SheetHeader>

        <SelectAllRow>
          <SelectAllLabel>
            <CheckBox
              checked={selected.size === contacts.length}
              onChange={toggleAll}
            />
            Selecionar todos
          </SelectAllLabel>
          <SelectedCount>{selected.size} selecionado{selected.size !== 1 ? 's' : ''}</SelectedCount>
        </SelectAllRow>

        <ListBody>
          {Array.from(grouped.entries()).map(([letter, items]) => (
            <AlphaSection key={letter}>
              <AlphaLabel>{letter}</AlphaLabel>
              {(items as { contact: DeviceContact; idx: number }[]).map(({ contact, idx }) => (
                <ContactRow key={idx}>
                  <CheckBox
                    checked={selected.has(idx)}
                    onChange={() => toggleOne(idx)}
                  />
                  <ContactAvatar $color={avatarColor(contact.rawName)}>
                    {initials(contact.rawName)}
                  </ContactAvatar>
                  <ContactInfo>
                    <ContactName>{contact.rawName}</ContactName>
                    <ContactPhone>{formatPhoneDisplay(contact.rawPhone)}</ContactPhone>
                  </ContactInfo>
                  <PencilBtn
                    onClick={() => setSingleEdit({
                      contact,
                      editedName: contact.rawName,
                      editedPhone: contact.rawPhone,
                      skipCity: true,
                    })}
                    title="Editar antes de importar"
                  >
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </PencilBtn>
                </ContactRow>
              ))}
            </AlphaSection>
          ))}
        </ListBody>

        <SheetFooter>
          <CancelBtn onClick={onClose}>Cancelar</CancelBtn>
          <ImportBtn onClick={handleBatchImport} disabled={selected.size === 0 || loading}>
            {loading ? <Spinner /> : null}
            {loading ? 'Importando...' : `Importar ${selected.size}`}
          </ImportBtn>
        </SheetFooter>
      </Sheet>

      {/* Single-contact edit sheet */}
      {singleEdit && (
        <>
          <EditSheet>
            <Handle />
            <SheetHeader>
              <SheetTitle>Editar contato</SheetTitle>
              <SheetSub>Confirme os dados antes de importar</SheetSub>
            </SheetHeader>
            <EditBody>
              <div>
                <EditLabel>Nome</EditLabel>
                <EditInput
                  value={singleEdit.editedName}
                  onChange={e => setSingleEdit(s => s ? { ...s, editedName: e.target.value } : s)}
                  autoFocus
                />
              </div>
              <div>
                <EditLabel>Telefone</EditLabel>
                <EditInput
                  value={singleEdit.editedPhone}
                  onChange={e => setSingleEdit(s => s ? { ...s, editedPhone: e.target.value } : s)}
                  type="tel"
                />
              </div>
              <ToggleRow>
                <div style={{ flex: 1 }}>
                  <ToggleLabel>Atribuir cidade depois</ToggleLabel>
                  <ToggleSub>Importa sem cidade; você completa na ficha do contato</ToggleSub>
                </div>
                <Toggle
                  checked={singleEdit.skipCity}
                  onChange={e => setSingleEdit(s => s ? { ...s, skipCity: e.target.checked } : s)}
                />
              </ToggleRow>
            </EditBody>
            <SheetFooter>
              <CancelBtn onClick={() => setSingleEdit(null)}>← Voltar</CancelBtn>
              <ImportBtn
                onClick={handleSingleSave}
                disabled={!singleEdit.editedName.trim() || !singleEdit.editedPhone.trim() || loading}
              >
                {loading ? <Spinner /> : null}
                {loading ? 'Salvando...' : 'Importar'}
              </ImportBtn>
            </SheetFooter>
          </EditSheet>
        </>
      )}
    </>
  );
};

export default ContactSyncModal;
